import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  TrendingDown, 
  User, 
  Users, 
  BookOpen, 
  MessageSquare,
  Calendar,
  Bell,
  ArrowRight,
  MoreVertical,
  CheckCircle,
  HelpCircle,
  BarChart,
  UserCheck,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Info
} from 'lucide-react';

// ... (imports remain same)
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

const ActionCenter = ({ coachingId }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [timeRange, setTimeRange] = useState('month'); // options: week, month, quarter, year

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const generateActions = useCallback(async () => {
    if (!coachingId) return;
    setRefreshing(true);
    try {
      // Calculate date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const rangeInDays = {
        week: 7,
        month: 30,
        quarter: 90,
        year: 365
      }[timeRange] || 30;

      const windowStart = new Date(today);
      windowStart.setDate(today.getDate() - rangeInDays);
      windowStart.setHours(0, 0, 0, 0);

      const windowStartTimestamp = Timestamp.fromDate(windowStart);

      // 1. Fetch Batches & Students
      let batches = [];
      let students = [];
      try {
        const batchesRef = collection(db, 'batches');
        const batchesQuery = query(batchesRef, where('coachingId', '==', coachingId));
        const batchesSnapshot = await getDocs(batchesQuery);
        batches = batchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const studentsRef = collection(db, 'students');
        const studentsQuery = query(studentsRef, where('coachingId', '==', coachingId));
        const studentsSnapshot = await getDocs(studentsQuery);
        students = studentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (e) {
        console.error('Error fetching initial data:', e);
      }

      if (batches.length === 0 && coachingId !== 'test-coaching-id' && coachingId !== 'demo-id') {
        setActions([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const batchIds = batches.map(b => b.id);

      // 2. Fetch Recent Attendance & Enrollments
      let attendance = [];
      let enrollments = [];
      try {
        const attendanceRef = collection(db, 'batchAttendance');
        const enrollmentsRef = collection(db, 'batchEnrollments');

        // CHUNKED ENROLLMENT FETCH (Optimization: in query vs map)
        const batchChunks = [];
        for (let i = 0; i < batchIds.length; i += 30) {
          batchChunks.push(batchIds.slice(i, i + 30));
        }

        const enrollmentPromises = batchChunks.map(chunk => 
          getDocs(query(enrollmentsRef, where('batchId', 'in', chunk)))
        );
        const enrollmentSnaps = await Promise.all(enrollmentPromises);
        enrollments = enrollmentSnaps.flatMap(snap => snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        // CHUNKED ATTENDANCE FETCH with Date Filter (Optimization: server-side date filtering)
        const attendanceChunks = [];
        for (let i = 0; i < batchIds.length; i += 10) {
          attendanceChunks.push(batchIds.slice(i, i + 10));
        }
        
        const attendancePromises = attendanceChunks.map(chunk => {
          // Attempt optimized query first
          const q = query(
            attendanceRef, 
            where('batchId', 'in', chunk),
            limit(1000)
          );
          return getDocs(q);
        });
        
        const results = await Promise.all(attendancePromises);
        attendance = results.flatMap(snap => snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        
        // Filter attendance in memory by date range (robust fallback for composite index issues)
        const windowStartDateStr = windowStart.toISOString().split('T')[0];
        attendance = attendance.filter(r => {
          const rDateStr = r.date || (r.markedAt?.toDate ? r.markedAt.toDate().toISOString().split('T')[0] : null);
          return rDateStr && rDateStr >= windowStartDateStr;
        });

        if (attendance.length === 0) {
          const oldRef = collection(db, 'attendance');
          const oldPromises = attendanceChunks.map(chunk => 
            getDocs(query(oldRef, where('batchId', 'in', chunk), limit(500)))
          );
          const oldResults = await Promise.all(oldPromises);
          attendance = oldResults.flatMap(snap => snap.docs.map(doc => doc.data())).filter(r => {
            const rDateStr = r.date || (r.markedAt?.toDate ? r.markedAt.toDate().toISOString().split('T')[0] : null);
            return rDateStr && rDateStr >= windowStartDateStr;
          });
        }
      } catch (e) {
        console.error('Error fetching attendance/enrollments:', e);
      }

      // 3. Fetch Recent Test Results (Optimization: server-side date filter)
      let testResults = [];
      try {
        const testResultsRef = collection(db, 'testResults');
        const studentResultsRef = collection(db, 'studentResults');
        
        const batchChunks = batchIds.slice(0, 10);
        const [testSnap, studentSnap] = await Promise.all([
          getDocs(query(testResultsRef, where('batchId', 'in', batchChunks), limit(300))),
          getDocs(query(studentResultsRef, where('batchId', 'in', batchChunks), limit(300)))
        ]);

        testResults = [
          ...testSnap.docs.map(doc => doc.data()),
          ...studentSnap.docs.map(doc => doc.data())
        ].filter(r => {
          const rDate = r.createdAt?.toDate ? r.createdAt.toDate() : (r.date ? new Date(r.date) : null);
          return !rDate || rDate >= windowStart;
        });
      } catch (e) {
        console.error('Error fetching test results:', e);
      }

      // 4. Fetch Pending Doubts (Optimization: server-side filtering)
      let doubts = [];
      try {
        const doubtsRef = collection(db, 'doubts');
        const batchDoubtsRef = collection(db, 'batchDoubts');
        
        const batchChunks = batchIds.slice(0, 10);
        const [doubtsSnap, batchDoubtsSnap] = await Promise.all([
          getDocs(query(doubtsRef, where('batchId', 'in', batchChunks), where('status', '==', 'pending'), limit(100))),
          getDocs(query(batchDoubtsRef, where('batchId', 'in', batchChunks), where('status', '==', 'pending'), limit(100)))
        ]);

        const allDoubts = [
          ...doubtsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          ...batchDoubtsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        ];
        
        doubts = allDoubts.filter(d => {
          const dDate = d.createdAt?.toDate ? d.createdAt.toDate() : (d.createdAt ? new Date(d.createdAt) : null);
          return !dDate || dDate >= windowStart;
        });
      } catch (e) {
        console.error('Error fetching doubts:', e);
      }

      // 5. Fetch Syllabus Progress (Optimization: limit batches)
      let syllabus = [];
      try {
        const syllabusRef = collection(db, 'batchSyllabus');
        const batchChunks = batchIds.slice(0, 15); // Limit analysis to top 15 batches for performance
        const syllabusPromises = batchChunks.map(bId => 
          getDocs(query(syllabusRef, where('batchId', '==', bId), limit(1)))
        );
        const results = await Promise.all(syllabusPromises);
        syllabus = results.flatMap(snap => snap.docs.map(doc => doc.data()));
      } catch (e) {
        console.error('Error fetching syllabus:', e);
      }

      let allAlerts = [];

      // Helper for name mapping
      const studentMap = {};
      students.forEach(s => {
        if (s.email) studentMap[s.email.toLowerCase()] = s.name || s.displayName;
        studentMap[s.id] = s.name || s.displayName;
      });

      const getStudentName = (email, id, fallback) => {
        const name = (email && studentMap[email.toLowerCase()]) || studentMap[id] || fallback;
        if (name === email && fallback && fallback !== email) return fallback;
        return name;
      };

      // A. Attendance Check
      if (enrollments.length > 0) {
        const validSchoolDates = [];
        let curr = new Date(windowStart);
        while (curr <= today) {
          if (curr.getDay() !== 0) { // Skip Sundays
            const dateStr = curr.toISOString().split('T')[0];
            validSchoolDates.push(dateStr);
          }
          curr.setDate(curr.getDate() + 1);
        }

        const studentStats = {};
        enrollments.forEach(enroll => {
          const sId = enroll.userId || enroll.studentId || enroll.id;
          const bId = enroll.batchId;
          const email = enroll.studentEmail || enroll.email || '';
          
          if (!studentStats[sId]) {
            const rawName = enroll.studentName || enroll.userData?.name || enroll.email || 'Student';
            const mappedName = getStudentName(email, sId, rawName);

            studentStats[sId] = {
              present: 0,
              total: validSchoolDates.length,
              name: mappedName,
              email: email,
              batchId: bId
            };
          }

          validSchoolDates.forEach(dateStr => {
            const record = attendance.find(r => 
              r.batchId === bId && 
              (r.studentId === sId || r.userId === sId) && 
              (r.date === dateStr || (r.markedAt?.toDate?.().toISOString().split('T')[0] === dateStr))
            );

            if (record && (record.status === 'present' || record.attendance === 'present')) {
              studentStats[sId].present++;
            }
          });
        });

        Object.entries(studentStats).forEach(([studentId, stats]) => {
          const missed = stats.total - stats.present;
          const rate = stats.total > 0 ? (stats.present / stats.total) * 100 : 100;
          const batch = batches.find(b => b.id === stats.batchId);

          if (stats.total > 0 && (rate < 85 || missed >= 3)) {
            allAlerts.push({
              id: `attendance-${studentId}`,
              category: 'attendance',
              type: 'low_attendance',
              severity: missed >= 5 ? 'critical' : 'moderate',
              studentName: stats.name,
              studentEmail: stats.email,
              batchName: batch?.name || 'Unknown Batch',
              details: `${stats.name} has missed ${missed} out of the last ${stats.total} working days. Attendance: ${rate.toFixed(1)}%.`,
              priority: missed >= 5 ? 10 : 7
            });
          }
        });
      }

      // B. Performance Check
      if (testResults.length > 0) {
        const studentScores = {};
        testResults.forEach(result => {
          const userId = result.userId || result.studentId || result.studentEmail;
          if (!userId) return;
          const email = result.studentEmail || result.userEmail || '';
          
          if (!studentScores[userId]) {
            const rawName = result.studentName || result.userName || result.studentEmail || 'Student';
            const mappedName = getStudentName(email, userId, rawName);

            studentScores[userId] = { 
              total: 0, 
              count: 0, 
              name: mappedName, 
              email: email,
              batchId: result.batchId 
            };
          }
          
          const score = parseFloat(result.score || result.marksObtained || 0);
          const maxMarks = parseFloat(result.maxMarks || result.totalMarks || 100);
          
          if (maxMarks > 0) {
            const scorePercent = (score / maxMarks) * 100;
            studentScores[userId].total += scorePercent;
            studentScores[userId].count++;
          }
        });

        Object.entries(studentScores).forEach(([userId, stats]) => {
          const avg = stats.total / stats.count;
          const batch = batches.find(b => b.id === stats.batchId);
          
          if (avg < 60) {
            allAlerts.push({
              id: `performance-${userId}`,
              category: 'performance',
              type: 'low_performance',
              severity: avg < 40 ? 'critical' : 'moderate',
              studentName: stats.name,
              studentEmail: stats.email,
              batchName: batch?.name || 'Unknown Batch',
              details: `${stats.name} is averaging ${avg.toFixed(1)}% across ${stats.count} test(s).`,
              priority: avg < 40 ? 9 : 6
            });
          }
        });
      }

      // C. Doubts Check
      doubts.forEach(doubt => {
        const doubtDate = doubt.createdAt?.toDate ? doubt.createdAt.toDate() : (doubt.createdAt ? new Date(doubt.createdAt) : new Date());
        const doubtAge = (Date.now() - doubtDate.getTime()) / (1000 * 60 * 60);
        const batch = batches.find(b => b.id === doubt.batchId);
        if (doubtAge > 12) {
          allAlerts.push({
            id: `doubt-${doubt.id}`,
            category: 'doubt',
            type: 'pending_doubt',
            severity: doubtAge > 24 ? 'moderate' : 'low',
            studentName: doubt.studentName || 'Student',
            batchName: batch?.name || 'Unknown Batch',
            details: `A doubt about "${doubt.subject || doubt.topic || 'Topic'}" has been pending for over ${Math.floor(doubtAge)} hours.`,
            priority: 5
          });
        }
      });

      // D. Syllabus Check
      const now = new Date();
      const currentYear = now.getFullYear();
      let sessionStart, sessionEnd;

      if (now.getMonth() >= 3) {
        sessionStart = new Date(currentYear, 3, 1);
        sessionEnd = new Date(currentYear + 1, 0, 31);
      } else {
        sessionStart = new Date(currentYear - 1, 3, 1);
        sessionEnd = new Date(currentYear, 0, 31);
      }

      const totalSessionDays = (sessionEnd - sessionStart) / (1000 * 60 * 60 * 24);
      const elapsedDays = Math.max(0, Math.min(totalSessionDays, (now - sessionStart) / (1000 * 60 * 60 * 24)));
      const expectedProgress = (elapsedDays / totalSessionDays) * 100;

      syllabus.forEach(s => {
        let totalTopics = 0;
        let completedTopics = 0;
        s.subjects?.forEach(subj => {
          subj.chapters?.forEach(chap => {
            chap.topics?.forEach(topic => {
              totalTopics++;
              if (topic.isDone) completedTopics++;
            });
          });
        });

        if (totalTopics > 0) {
          const progress = (completedTopics / totalTopics) * 100;
          const batch = batches.find(b => b.id === s.batchId);
          
          if (progress < expectedProgress - 10 && batch) {
            allAlerts.push({
              id: `syllabus-${s.batchId}`,
              category: 'syllabus',
              type: 'syllabus_lag',
              severity: (expectedProgress - progress) > 25 ? 'moderate' : 'low',
              batchName: batch.name,
              details: `Batch "${batch.name}" is at ${progress.toFixed(1)}% completion. Targeted progress: ${expectedProgress.toFixed(1)}%.`,
              priority: (expectedProgress - progress) > 25 ? 6 : 4
            });
          }
        }
      });

      // Grouping Logic
      const groupedActions = [];
      const categoryConfig = {
        attendance: {
          icon: Calendar,
          color: 'red',
          title: 'Attendance Concerns',
          impact: 'High risk of learning gaps and student disengagement.',
          actions: ['Notify Parents', 'Schedule Counseling', 'Review Attendance Policy']
        },
        performance: {
          icon: TrendingDown,
          color: 'orange',
          title: 'Performance Warnings',
          impact: 'Risk of failing upcoming assessments or falling behind in curriculum.',
          actions: ['Assign Remedial Work', 'Notify Parent', 'Schedule Doubt Session']
        },
        doubt: {
          icon: HelpCircle,
          color: 'blue',
          title: 'Unresolved Doubts',
          impact: 'Delays in conceptual clarity may hinder future progress.',
          actions: ['Remind Teacher', 'Resolve Manually', 'Assign Assistant']
        },
        syllabus: {
          icon: BookOpen,
          color: 'purple',
          title: 'Syllabus Delays',
          impact: 'Potential rush at session end, affecting revision time and exam readiness.',
          actions: ['Reschedule Classes', 'Add Extra Sessions', 'Review Lesson Plan']
        }
      };

      const categories = ['attendance', 'performance', 'doubt', 'syllabus'];
      categories.forEach(cat => {
        const catAlerts = allAlerts.filter(a => a.category === cat);
        if (catAlerts.length > 0) {
          const highestPriority = Math.max(...catAlerts.map(a => a.priority));
          const severities = catAlerts.map(a => a.severity);
          const finalSeverity = severities.includes('critical') ? 'critical' : (severities.includes('moderate') ? 'moderate' : 'low');
          
          groupedActions.push({
            id: `group-${cat}`,
            category: cat,
            title: categoryConfig[cat].title,
            count: catAlerts.length,
            impact: categoryConfig[cat].impact,
            actions: categoryConfig[cat].actions,
            icon: categoryConfig[cat].icon,
            color: categoryConfig[cat].color,
            priority: highestPriority,
            severity: finalSeverity,
            items: catAlerts.sort((a, b) => b.priority - a.priority)
          });
        }
      });

      setActions(groupedActions.sort((a, b) => b.priority - a.priority));
    } catch (error) {
      console.error('Error generating actions:', error);
      toast.error('Failed to analyze data for actions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [coachingId, timeRange]);

  useEffect(() => {
    generateActions();
  }, [generateActions, timeRange]);

  const handleAction = (action, groupTitle) => {
    toast.success(`${action} initiated for ${groupTitle}`);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400 font-medium">Analyzing daily insights...</p>
      </div>
    );
  }

  const timeRangeOptions = [
    { id: 'week', label: 'Last Week' },
    { id: 'month', label: 'Last Month' },
    { id: 'quarter', label: 'Last Quarter' },
    { id: 'year', label: 'Last Year' }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Bell className="w-6 h-6 mr-2 text-blue-600" />
            Action Center
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Grouped priorities with impact statements and actionable steps
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-xl border border-gray-200 dark:border-gray-700">
            {timeRangeOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => setTimeRange(option.id)}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  timeRange === option.id 
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <button 
            onClick={generateActions}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors font-bold text-sm shadow-md shadow-blue-500/20 disabled:opacity-50"
          >
            <Clock className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Analyzing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {actions.length === 0 ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-10 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-green-900 dark:text-green-100">You're all caught up!</h3>
          <p className="text-green-700 dark:text-green-300 mt-2 max-w-md mx-auto">
            Our analysis shows all students and batches are performing within target ranges today. Great job!
          </p>
        </div>
      ) : (
        <div className="grid gap-6 max-h-[75vh] overflow-y-auto pr-2 custom-scrollbar">
          {actions.map((group) => {
            const Icon = group.icon;
            const isExpanded = expandedItems[group.id];
            
            const severityStyles = {
              critical: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400',
              moderate: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-900/40 text-orange-700 dark:text-orange-400',
              low: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/40 text-blue-700 dark:text-blue-400'
            }[group.severity];

            const badgeStyles = {
              critical: 'bg-red-600 text-white shadow-sm',
              moderate: 'bg-orange-500 text-white shadow-sm',
              low: 'bg-blue-500 text-white shadow-sm'
            }[group.severity];

            return (
              <div 
                key={group.id}
                className={`flex flex-col rounded-xl border transition-all ${severityStyles} shadow-sm overflow-hidden`}
              >
                {/* Header section */}
                <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-xl flex-shrink-0 ${badgeStyles}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-xl tracking-tight">{group.title}</span>
                        <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${badgeStyles}`}>
                          {group.severity}
                        </span>
                        <span className="text-sm font-bold opacity-75">
                          ({group.count} {group.count === 1 ? 'issue' : 'issues'})
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm font-semibold opacity-90 italic">
                        Impact: {group.impact}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleExpand(group.id)}
                      className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-white/50 dark:bg-black/20 hover:bg-white/80 dark:hover:bg-black/40 transition-colors text-sm font-bold border border-current/10"
                    >
                      <span>{isExpanded ? 'Hide Details' : 'View Details'}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Details section */}
                {isExpanded && (
                  <div className="px-5 pb-5 animate-in slide-in-from-top-2 duration-300">
                    <div className="bg-white/40 dark:bg-black/10 rounded-lg p-4 mb-4 border border-current/5">
                      <h4 className="text-xs font-bold uppercase tracking-widest mb-3 opacity-60">Specific Issues:</h4>
                      <div className="space-y-3">
                        {group.items.map((item, idx) => (
                          <div key={item.id} className="flex items-start space-x-2 text-sm">
                            <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" />
                            <div>
                              <span className="font-bold">{item.studentName || item.batchName}</span>
                              {item.studentEmail && <span className="text-xs opacity-70 ml-1">({item.studentEmail})</span>}
                              {item.batchName && item.studentName && <span className="text-xs opacity-70 ml-1">• {item.batchName}</span>}
                              <p className="mt-0.5 opacity-80">{item.details}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-4">
                      <span className="text-xs font-bold uppercase tracking-widest opacity-60 w-full md:w-auto mr-2">Recommended Actions:</span>
                      {group.actions.map((actionLabel) => (
                        <button
                          key={actionLabel}
                          onClick={() => handleAction(actionLabel, group.title)}
                          className="px-4 py-2 rounded-lg bg-white dark:bg-gray-800 text-xs font-bold uppercase tracking-wider shadow-sm hover:shadow-md transition-all border border-current/10 active:scale-95"
                        >
                          {actionLabel}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Footer */}
      {actions.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 pt-6 border-t border-gray-100 dark:border-gray-800">
          <p>Analyzing {actions.reduce((acc, g) => acc + g.count, 0)} student & batch data points for the {timeRange === 'week' ? 'last 7 days' : timeRange === 'month' ? 'last 30 days' : timeRange === 'quarter' ? 'last 90 days' : 'last year'}</p>
          <div className="flex items-center space-x-5">
            <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-red-500 mr-2 shadow-[0_0_8px_rgba(239,68,68,0.5)]"></span> Critical</span>
            <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-orange-500 mr-2 shadow-[0_0_8px_rgba(249,115,22,0.5)]"></span> Moderate</span>
            <span className="flex items-center"><span className="w-2 h-2 rounded-full bg-blue-500 mr-2 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></span> Low</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionCenter;
